import React, { Component, type ReactNode } from 'react';
import posthog from 'posthog-js';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  /** Content rendered when no error is present. */
  children: ReactNode;
  /** Optional custom fallback UI to render on error. */
  fallback?: ReactNode;
  /** Optional override for showing dev-only error details (tests only). */
  isDevOverride?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary component that catches React rendering errors
 * and integrates with PostHog for error tracking
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Capture error in PostHog
    try {
      posthog.capture('$exception', {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace_raw: error.stack,
        $exception_sources: errorInfo.componentStack,
        $exception_level: 'error',
      });
    } catch (posthogError) {
      // Fail silently if PostHog is not initialized
      console.error('Failed to capture error in PostHog:', posthogError);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const inferredDev = import.meta.env.DEV === true;
    const isDev = this.props.isDevOverride ?? inferredDev;
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          isDev={isDev}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isDev: boolean;
  onReset: () => void;
}

/**
 * Default error fallback UI component
 */
const ErrorFallback = ({ error, errorInfo, isDev, onReset }: ErrorFallbackProps): JSX.Element => {
  return (
    <div className="error-boundary">
      <div className="error-boundary__container">
        <div className="error-boundary__icon" aria-hidden="true">
          ⚠️
        </div>
        <h1 className="error-boundary__title">Something went wrong</h1>
        <p className="error-boundary__message">
          We're sorry, but something unexpected happened. Our team has been notified and is working
          on a fix.
        </p>

        {isDev && error && (
          <details className="error-boundary__details">
            <summary className="error-boundary__summary">Error Details (Development Only)</summary>
            <div className="error-boundary__error-content">
              <div className="error-boundary__error-section">
                <strong>Error:</strong>
                <pre className="error-boundary__pre">{error.toString()}</pre>
              </div>
              {error.stack && (
                <div className="error-boundary__error-section">
                  <strong>Stack Trace:</strong>
                  <pre className="error-boundary__pre">{error.stack}</pre>
                </div>
              )}
              {errorInfo?.componentStack && (
                <div className="error-boundary__error-section">
                  <strong>Component Stack:</strong>
                  <pre className="error-boundary__pre">{errorInfo.componentStack}</pre>
                </div>
              )}
            </div>
          </details>
        )}

        <div className="error-boundary__actions">
          <button
            type="button"
            onClick={onReset}
            className="error-boundary__button error-boundary__button--primary"
          >
            Try Again
          </button>
          <button
            type="button"
            onClick={() => (window.location.href = '/')}
            className="error-boundary__button error-boundary__button--secondary"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
